import { createElement, createRef, PureComponent } from 'react';
import {
    Box,
    Dialog,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    listItemButtonClasses,
    listItemClasses,
    styled,
} from '@mui/material';
import { MoreIcon } from '../icons';
import type { DesktopMenuItemProps } from './DesktopMenuItem';
import DesktopMenuItem from './DesktopMenuItem';
import { MyNavLink } from './MyNavLink.tsx';

const hasModalOnly = (items: ReadonlyArray<{ modalOnly?: boolean }>) =>
    items.some((item) => item.modalOnly);

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
export const MainNavigation = (props: MainNavigationProps) => {
    return <MainNavigationInner {...props} width="xs" />;
};

const MobileListItem = styled(ListItem, {
    shouldForwardProp: (prop) =>
        !['sx', 'cta', 'iconOnly'].includes(prop as string),
})<{ cta: boolean; iconOnly: boolean }>(({ theme, cta, iconOnly }) => ({
    ...(cta
        ? {
              color: theme.bino.color.white,
              backgroundColor:
                  theme.palette.mode === 'dark'
                      ? theme.palette.background.paper
                      : theme.bino.color.grayishBlue,
          }
        : {
              color: theme.bino.labelColor,
          }),
    ...(iconOnly
        ? {
              width: 56,
              height: 'calc(100% - 1px)',
          }
        : {}),
    '& > .active': {
        color: theme.palette.primary.main,
    },
    padding: 0,
    height: '100%',
    lineHeight: '16px',
    whiteSpace: `nowrap`,
    textDecoration: 'none',
    [`& > .${listItemButtonClasses.root}`]: {
        fontSize: '11px',
        // width: "100%",
        height: '100%',
        textAlign: `center`,
        letterSpacing: '1px',
        fontWeight: 'bold',
        lineHeight: 1,
        textDecoration: 'none',
        textTransform: 'uppercase',
        justifyContent: 'center',
        padding: 0,
        // color: 'inherit',
    },
    '&:not(:first-of-type) label': {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
}));

export interface ItemOptions extends DesktopMenuItemProps {
    modalOnly?: boolean;
    modalPriority?: number;
    iconOnlyOnMobile?: boolean;
}

export interface MainNavigationProps {
    dashboard?: boolean;
    mobile?: boolean;
    items?: Array<ItemOptions>;
}
type MainNavigationInnerProps = MainNavigationProps & {
    width: string;
};

type MainNavigationInnerState = {
    showModal: boolean;
    renderModal: boolean;
    showModalLink: boolean;
};

class MainNavigationInner extends PureComponent<
    MainNavigationInnerProps,
    MainNavigationInnerState
> {
    state = {
        showModal: false,
        renderModal: false,
        showModalLink: false,
    };

    ref = createRef<HTMLDivElement>();

    componentDidMount() {
        window.addEventListener('resize', this.onWindowResize);
        this.checkMobileMenuOverflow();
    }

    componentDidUpdate() {
        this.checkMobileMenuOverflow();
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.onWindowResize);
    }

    onWindowResize = () => {
        this.checkMobileMenuOverflow();
    };

    checkMobileMenuOverflow() {
        const { mobile, items } = this.props;
        if (!mobile) {
            return;
        }
        const visibleItems = items.filter(({ modalOnly }) => !modalOnly);

        // Make space for the modal icon
        const modalIconSize = 48;
        const node = this.ref.current;
        const maxWidth = node.offsetWidth - modalIconSize;
        const itemsAndNodes: { node: HTMLElement; item: ItemOptions }[] =
            Array.from(
                node.querySelectorAll<HTMLElement>(`.${listItemClasses.root}`),
            ).map((node, index) => {
                return {
                    node,
                    item: visibleItems[index],
                };
            });
        itemsAndNodes.sort(
            (
                { item: { modalPriority: a = 0 } },
                { item: { modalPriority: b = 0 } },
            ) => a - b,
        );

        let accumulatedWidth = 0;
        itemsAndNodes.forEach(({ node }) => {
            const labelNode = node.querySelector<HTMLElement>(`label`);

            accumulatedWidth += labelNode.clientWidth;

            if (accumulatedWidth > maxWidth) {
                node.style.position = 'absolute';
                node.style.visibility = 'hidden';
            } else {
                node.style.position = '';
                node.style.visibility = '';
            }
        });

        const showModalLink =
            accumulatedWidth > maxWidth || hasModalOnly(items);
        if (this.state.showModalLink !== showModalLink) {
            this.setState({ showModalLink });
        }
    }

    render() {
        const { items, mobile, width } = this.props;

        const isMobile =
            mobile === undefined ? width === 'xs' || width === 'sm' : mobile;

        const renderItem = isMobile
            ? this.renderMobileItem.bind(this)
            : this.renderDesktopItem.bind(this);
        const showModalLink = isMobile && this.state.showModalLink;
        return (
            <Box position="relative" ref={this.ref}>
                <List
                    component="nav"
                    sx={
                        isMobile
                            ? (theme) => ({
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  overflow: 'hidden',
                                  height: 48,
                                  borderBottom: `solid 1px ${theme.bino.color.boxShadowBorder}`,
                                  padding: 0,
                              })
                            : {
                                  p: 0,
                              }
                    }
                >
                    {items
                        .filter(({ modalOnly = false }) => false === modalOnly)
                        .map(renderItem)}
                    {showModalLink && this.renderModalLink()}
                </List>
                {this.state.renderModal && this.renderModal()}
            </Box>
        );
    }

    renderDesktopItem(item: any) {
        const { dashboard } = this.props;
        return (
            <DesktopMenuItem key={item.to} {...item} dashboard={dashboard} />
        );
    }

    renderMobileItem({
        to,
        exact,
        label,
        cta,
        iconComponent,
        iconOnlyOnMobile,
    }: any) {
        return (
            <MobileListItem key={to} cta={cta} iconOnly={iconOnlyOnMobile}>
                <ListItemButton component={MyNavLink} end={exact} to={to}>
                    <label>
                        {iconOnlyOnMobile
                            ? createElement(iconComponent, { size: 24 })
                            : label}
                    </label>
                </ListItemButton>
            </MobileListItem>
        );
    }

    renderModalItem(item: any) {
        // In case we click on the active page, nothing happens, so we need to force closing the dialog
        return this.renderDesktopItem({
            ...item,
            onClick: () => {
                this.setState({
                    showModal: false,
                });
            },
        });
    }

    renderModalLink() {
        return (
            <IconButton onClick={this.onClickShowModal} size="large">
                <MoreIcon />
            </IconButton>
        );
    }

    renderModal() {
        const { showModal } = this.state;
        const { items } = this.props;

        const renderItem = this.renderModalItem.bind(this);

        return (
            <Dialog
                fullScreen
                open={showModal}
                TransitionProps={{
                    onExited: () => this.setState({ renderModal: false }),
                }}
                onClose={() => this.setState({ showModal: false })}
            >
                <IconButton
                    onClick={this.onClickHideModal}
                    style={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        zIndex: 1,
                    }}
                    size="large"
                >
                    X
                </IconButton>
                <List component="nav">{items.map(renderItem)}</List>
            </Dialog>
        );
    }

    onClickShowModal = (event: any) => {
        event.preventDefault();
        this.setState({
            showModal: true,
            renderModal: true,
        });
    };

    onClickHideModal = (event: any) => {
        event.preventDefault();
        this.setState({
            showModal: false,
        });
    };
}
